import React, { useState, useEffect } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
  Box,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  CircularProgress,
  Tooltip,
  IconButton,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { Chip } from '@mui/material';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { db } from '../firebase';
import { collection, getDocs, doc, deleteDoc, query, orderBy } from 'firebase/firestore';
import styles from '../styles/adminActasStyles';

const AdminActas = ({ onActaDeleted, onEditActa }) => {
  const [actas, setActas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [actaToDelete, setActaToDelete] = useState(null);
  const [deleteStatus, setDeleteStatus] = useState({ success: false, error: null });

  // Cargar todas las actas al iniciar el componente
  useEffect(() => {
    fetchActas();
  }, []);

  const fetchActas = async () => {
    try {
      setLoading(true);
      const actasRef = collection(db, 'actas');
      const q = query(actasRef, orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      
      const actasData = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        // Convertir las fechas de Firestore a objetos Date
        const acta = {
          ...data,
          id: doc.id,
          fecha: data.fecha ? data.fecha.toDate() : new Date(),
          horaInicio: data.horaInicio ? data.horaInicio.toDate() : new Date(),
          horaFinal: data.horaFinal ? data.horaFinal.toDate() : new Date(),
        };
        actasData.push(acta);
      });
      
      setActas(actasData);
      setError(null);
    } catch (error) {
      console.error('Error al cargar actas:', error);
      setError('Error al cargar las actas. Por favor intente nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  // Abrir diálogo de confirmación para eliminar acta
  const handleDeleteClick = (acta) => {
    setActaToDelete(acta);
    setDeleteDialogOpen(true);
  };

  // Cerrar diálogo de confirmación
  const handleCloseDialog = () => {
    setDeleteDialogOpen(false);
    setActaToDelete(null);
  };

  // Eliminar acta
  const handleDeleteConfirm = async () => {
    if (!actaToDelete) return;
    
    try {
      setLoading(true);
      // Eliminar el acta de Firestore
      await deleteDoc(doc(db, 'actas', actaToDelete.id));
      
      // Actualizar la lista de actas local
      setActas(prevActas => prevActas.filter(acta => acta.id !== actaToDelete.id));
      
      // Preparar detalles del acta para el log
      const actaDetails = {
        numeroActa: actaToDelete.numeroActa,
        tipoActa: actaToDelete.tipoActa,
        tipoProyecto: actaToDelete.tipoProyecto,
        productoProyecto: actaToDelete.productoProyecto || ''
      };
      
      // Notificar al componente padre (App) que se ha eliminado un acta
      // para que actualice el estado global y HistorialActas se actualice
      // Pasar los detalles del acta para el registro de logs
      if (onActaDeleted && typeof onActaDeleted === 'function') {
        onActaDeleted(actaToDelete.id, actaDetails);
      }
      
      // Mostrar mensaje de éxito
      setDeleteStatus({ success: true, error: null });
      
      // Limpiar mensaje después de 3 segundos
      setTimeout(() => {
        setDeleteStatus({ success: false, error: null });
      }, 3000);
      
      // Cerrar el diálogo
      handleCloseDialog();
      
      // Actualizar la numeración global de actas
      if (window.actaNumbering) {
        // El número de acta eliminado ahora está disponible para su reutilización
        // No necesitamos hacer nada más aquí, ya que la numeración se calcula
        // dinámicamente al crear una nueva acta
      }
    } catch (error) {
      console.error('Error al eliminar acta:', error);
      setDeleteStatus({ success: false, error: 'Error al eliminar el acta. Por favor intente nuevamente.' });
      
      // Limpiar mensaje de error después de 3 segundos
      setTimeout(() => {
        setDeleteStatus({ success: false, error: null });
      }, 3000);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight={500} color="primary">
          Administración de Actas
        </Typography>
      </Box>
      
      {deleteStatus.success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Acta eliminada correctamente. El número de acta ahora está disponible para su reutilización.
        </Alert>
      )}
      
      {deleteStatus.error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {deleteStatus.error}
        </Alert>
      )}
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {loading && actas.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell>Número</TableCell>
                <TableCell>Fecha</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell>Contrato</TableCell>
                <TableCell>Proyecto</TableCell>
                <TableCell>Responsable</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell align="center">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {actas.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    No hay actas registradas
                  </TableCell>
                </TableRow>
              ) : (
                actas.map((acta) => {
                  const isBorrador = acta.approvalStatus === 'borrador';
                  
                  return (
                    <TableRow key={acta.id}>
                      <TableCell>{acta.numeroActa}</TableCell>
                      <TableCell>{format(acta.fecha, 'dd/MM/yyyy', { locale: es })}</TableCell>
                      <TableCell>{acta.tipoActa}</TableCell>
                      <TableCell>{acta.tipoProyecto}</TableCell>
                      <TableCell>{acta.productoProyecto || '-'}</TableCell>
                      <TableCell>{acta.responsable}</TableCell>
                      <TableCell>
                        <Chip 
                          label={isBorrador ? 'Borrador' : (acta.approvalStatus || 'Por aprobar')}
                          color={isBorrador ? 'warning' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        {isBorrador ? (
                          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                            <Tooltip title="Editar borrador">
                              <IconButton 
                                color="primary" 
                                onClick={() => onEditActa && onEditActa(acta)}
                                size="small"
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Eliminar borrador">
                              <IconButton 
                                color="error" 
                                onClick={() => {
                                  if (window.confirm('¿Está seguro de eliminar este borrador de acta?')) {
                                    handleDeleteClick(acta);
                                  }
                                }}
                                size="small"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        ) : (
                          <Tooltip title="Eliminar acta">
                            <IconButton 
                              color="error" 
                              onClick={() => handleDeleteClick(acta)}
                              size="small"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      
      {/* Diálogo de confirmación para eliminar acta */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          Confirmar eliminación
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            ¿Está seguro que desea eliminar el acta número {actaToDelete?.numeroActa} del contrato {actaToDelete?.tipoProyecto}?
            <br /><br />
            <strong>Esta acción no se puede deshacer.</strong>
            <br /><br />
            Al eliminar esta acta, su número quedará disponible para futuras actas del mismo contrato/proyecto.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary">
            Cancelar
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" autoFocus>
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default AdminActas;